import React from "react";
import useRevealOnScroll from "../hooks/useRevealOnScroll";

const PRESIDENT = {
  role: "President",
  name: "Norbert Mendonca",
  parish: "O.L.OF LOURDES - ORLEM",
  image: "/office-bearers/president.webp",
};

const VICE_PRESIDENTS = [
  {
    role: "Vice President",
    name: "Alex DSouza",
    parish: "O.L.OF ROSARY - GOREGAON",
    image: "/office-bearers/vice-president1.webp",
  },
  {
    role: "Vice President",
    name: "Kevin DSouza",
    parish: "ST. JUDE - JERI MERI",
    image: "/office-bearers/vice-president2.webp",
  },
];

const COUNCIL = [
  {
    role: "Secretary General",
    name: "Francis Colaco",
    parish: "O.L.OF GOOD COUNSEL - SION",
    image: "/office-bearers/secretary-graneral.webp",
  },
  {
    role: "Treasurer",
    name: "CA. Ajoy Fernandes",
    parish: "O.L.OF VICTORIES - MAHIM",
    image: "/office-bearers/treasurer.webp",
  },
  {
    role: "Legal Advisor",
    name: "Adv. Elton George",
    parish: "O.L.OF LOURDES - ORELM",
    image: "/office-bearers/legal-advisor.webp",
  },
];

function BearerCard({ bearer, variant = "default", delay = 0 }) {
  const [ref, visible] = useRevealOnScroll();
  const isLead = variant === "lead";

  return (
    <article
      ref={ref}
      className={`reveal-on-scroll ${visible ? "is-visible" : ""} group`}
      style={{ "--reveal-delay": `${delay}ms` }}
      data-testid={`office-bearer-${bearer.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div
        className={[
          "relative h-full rounded-2xl border bg-white text-center transition-all duration-500",
          isLead
            ? "border-burgundy-600/25 px-8 py-10 shadow-[0_20px_50px_-24px_rgba(122,31,43,0.35)] group-hover:shadow-[0_28px_60px_-22px_rgba(122,31,43,0.42)] group-hover:-translate-y-1"
            : "border-line px-5 py-7 shadow-card group-hover:shadow-card-hover group-hover:border-burgundy-600/20 group-hover:-translate-y-0.5",
        ].join(" ")}
      >
        <div
          className={[
            "relative mx-auto rounded-full p-1 bg-gradient-to-br from-gold-300 via-gold-500 to-gold-600 shadow-md",
            isLead ? "w-40 h-40 sm:w-44 sm:h-44" : "w-28 h-28 sm:w-32 sm:h-32",
          ].join(" ")}
        >
          <div className="w-full h-full rounded-full overflow-hidden bg-ivory-100 ring-2 ring-white">
            <img
              src={bearer.image}
              alt={bearer.name}
              width={isLead ? 176 : 128}
              height={isLead ? 176 : 128}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
            />
          </div>
        </div>

        <p className="mt-5 text-[10px] sm:text-[11px] font-semibold tracking-[0.2em] uppercase text-gold-600">
          {bearer.role}
        </p>
        <h3
          className={[
            "mt-2 font-heading font-semibold text-ink-900 leading-snug",
            isLead ? "text-xl sm:text-2xl" : "text-base sm:text-lg",
          ].join(" ")}
        >
          {bearer.name}
        </h3>
        <p className="mt-2 text-xs sm:text-sm text-ink-500 leading-relaxed">
          {bearer.parish}
        </p>
      </div>
    </article>
  );
}

function Connector({ className = "" }) {
  return (
    <div
      className={`org-connector ${className}`}
      aria-hidden="true"
    >
      <span className="org-connector-line" />
    </div>
  );
}

export default function OfficeBearersSection() {
  const [headerRef, headerVisible] = useRevealOnScroll();

  return (
    <section
      id="leadership"
      className="py-20 sm:py-28 bg-ivory-50 border-t border-line overflow-hidden"
      data-testid="office-bearers-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div
          ref={headerRef}
          className={`reveal-on-scroll max-w-3xl mx-auto text-center ${headerVisible ? "is-visible" : ""}`}
        >
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gold-500 mb-4">
            Leadership
          </p>
          <h2 className="font-heading font-semibold text-ink-900 text-3xl sm:text-4xl leading-tight tracking-tight">
            Current office bearers
          </h2>
          <div className="gold-rule mx-auto my-7" aria-hidden="true" />
          <p className="text-ink-700 leading-relaxed">
            The elected leadership guiding The Bombay Catholic Sabha across parishes,
            deaneries and forums throughout the Archdiocese of Bombay.
          </p>
        </div>

        <div className="mt-16 sm:mt-20 max-w-4xl mx-auto">
          {/* President */}
          <div className="max-w-sm mx-auto">
            <BearerCard bearer={PRESIDENT} variant="lead" delay={0} />
          </div>

          <Connector className="my-6 sm:my-8 h-10 sm:h-12" />

          {/* Vice Presidents */}
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 max-w-2xl mx-auto">
            {VICE_PRESIDENTS.map((bearer, i) => (
              <BearerCard key={bearer.name} bearer={bearer} delay={120 + i * 100} />
            ))}
          </div>

          <Connector className="my-6 sm:my-8 h-10 sm:h-12" />

          {/* Council */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {COUNCIL.map((bearer, i) => (
              <BearerCard key={bearer.name} bearer={bearer} delay={320 + i * 90} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
